// ============================================================================
// GST MAPPING PROFILE — FORM ⇄ DEFINITION (ADR-0025 §5)
// ============================================================================
// The mapper edits a form; the server stores the definition JSON the engine
// reads. This file is the only place that converts between them, and it is
// pure so it can be reasoned about without the modal.
//
// The form covers what a client's own register needs: a column per field,
// matched by header (with occurrence for a repeated header such as Tally's two
// "CGST" columns, or by position when the header is blank), a date format per
// sheet, rate-wise column groups for a rate-pivoted register, and a unit map.
// A definition using anything else — summed columns, constants, continuation
// rows — does not fit the form; `fromDefinition` says so and the mapper edits
// it as JSON instead, so nothing is silently dropped on a round trip.

export interface ColumnChoice {
  /** 0-based index into the sheet's header cells; null = not mapped. */
  index: number | null;
}

export interface RateGroupForm {
  rate: string;
  columns: Record<string, number | null>; // taxable_value / igst / cgst / sgst / cess → column index
}

export interface UnitMapRow {
  from: string;
  to: string;
}

export interface SheetForm {
  sheet: string;
  include: boolean;
  /** Sheet-name pattern; * matches any run of characters (a month in the name, say). */
  match: string;
  recordKind: 'LINE' | 'DOCUMENT' | 'SUMMARY';
  dateFormat: string;
  /** Dates stored as plain numbers (Excel serials) rather than date cells or text. */
  allowSerial: boolean;
  fields: Record<string, number | null>;
  required: string[];
  groups: RateGroupForm[];
  unitMap: UnitMapRow[];
}

export interface ProfileForm {
  sheets: SheetForm[];
}

export const GROUP_FIELDS = ['taxable_value', 'igst', 'cgst', 'sgst', 'cess'] as const;

/** Display names for the mapping vocabulary (the API's field names are snake_case). */
export const FIELD_LABELS: Record<string, string> = {
  document_number: 'Doc no', document_date: 'Date', voucher_date: 'Voucher date', document_value: 'Doc value',
  note_type: 'Note', original_document_number: 'Orig doc', original_document_date: 'Orig date',
  counterparty_gstin: 'GSTIN', counterparty_name: 'Party', place_of_supply: 'POS', reverse_charge: 'RC',
  supply_type: 'Type', ecommerce_gstin: 'E-com GSTIN', branch_name: 'Branch', item_code: 'Item',
  description: 'Description', hsn: 'HSN', uqc: 'UQC', quantity: 'Qty', rate: 'Rate %', taxable_value: 'Taxable',
  igst: 'IGST', cgst: 'CGST', sgst: 'SGST', cess: 'Cess', total_value: 'Total value', round_off: 'Round off',
  nature: 'Nature', serial_from: 'From', serial_to: 'To', total_number: 'Total', cancelled: 'Cancelled',
};

/** Longer names for the mapper, where there is room. */
export const FIELD_NAMES: Record<string, string> = {
  ...FIELD_LABELS,
  document_number: 'Invoice / document number', document_date: 'Invoice date', document_value: 'Invoice value (incl. tax)',
  note_type: 'Note type (C / D)', original_document_number: 'Original invoice number', original_document_date: 'Original invoice date',
  counterparty_gstin: 'Customer / supplier GSTIN', counterparty_name: 'Customer / supplier name', place_of_supply: 'Place of supply',
  reverse_charge: 'Reverse charge (Y/N)', supply_type: 'Supply type', hsn: 'HSN / SAC', uqc: 'Unit (UQC)', quantity: 'Quantity',
  rate: 'GST rate %', taxable_value: 'Taxable value',
};

/** The fields most registers carry — shown first; the rest sit behind "all fields". */
export const CORE_FIELDS = [
  'document_number', 'document_date', 'counterparty_gstin', 'counterparty_name', 'place_of_supply', 'document_value',
  'hsn', 'description', 'uqc', 'quantity', 'rate', 'taxable_value', 'igst', 'cgst', 'sgst', 'cess',
];

export const DATE_FORMATS = [
  'dd-MM-yyyy', 'dd/MM/yyyy', 'dd.MM.yyyy', 'yyyy-MM-dd', 'dd-MMM-yyyy', 'dd-MMM-yy',
  'd-M-yyyy', 'd/M/yyyy', 'dd/MM/yy', 'MM/dd/yyyy',
];

/** Same normalisation as the engine's NormaliseHeader: trim, collapse whitespace, lower-case. */
export const normaliseHeader = (h: string | null | undefined): string =>
  (h ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

/** "CGST", or "CGST (2nd)" when the header repeats — what the mapper shows in a column picker. */
export const columnLabel = (headers: string[], index: number): string => {
  const h = headers[index]?.trim();
  if (!h) return `Column ${index + 1} (no header)`;
  const want = normaliseHeader(h);
  const total = headers.filter((x) => normaliseHeader(x) === want).length;
  if (total < 2) return h;
  const nth = headers.slice(0, index + 1).filter((x) => normaliseHeader(x) === want).length;
  const suffix = nth === 1 ? '1st' : nth === 2 ? '2nd' : nth === 3 ? '3rd' : `${nth}th`;
  return `${h} (${suffix})`;
};

const refFor = (headers: string[], index: number): Record<string, unknown> => {
  const h = headers[index]?.trim();
  if (!h) return { index: index + 1 };
  const want = normaliseHeader(h);
  const nth = headers.slice(0, index + 1).filter((x) => normaliseHeader(x) === want).length;
  return nth > 1 ? { header: h, occurrence: nth } : { header: h };
};

/** The definition JSON for a form — the exact shape seed 058 and the engine use. */
export const toDefinition = (form: ProfileForm, headersOf: (sheet: string) => string[]): string => {
  const sheets = form.sheets.filter((s) => s.include).map((s) => {
    const headers = headersOf(s.sheet);
    const fields: Record<string, unknown> = {};
    Object.entries(s.fields).forEach(([name, idx]) => {
      if (idx === null || idx === undefined) return;
      const ref = refFor(headers, idx);
      if (s.allowSerial && /date/.test(name)) ref.allowSerial = true;
      fields[name] = ref;
    });

    const groups = s.groups
      .filter((g) => g.rate.trim() !== '' && Object.values(g.columns).some((v) => v !== null && v !== undefined))
      .map((g) => {
        const gf: Record<string, unknown> = {};
        Object.entries(g.columns).forEach(([name, idx]) => {
          if (idx !== null && idx !== undefined) gf[name] = refFor(headers, idx);
        });
        return { rate: Number(g.rate), fields: gf };
      });

    const valueMaps: Record<string, Record<string, string>> = {};
    const units = s.unitMap.filter((u) => u.from.trim() && u.to.trim());
    if (units.length) valueMaps.uqc = Object.fromEntries(units.map((u) => [u.from.trim(), u.to.trim().toUpperCase()]));

    const entry: Record<string, unknown> = {
      match: [s.match.trim() || s.sheet],
      recordKind: s.recordKind,
      dateFormats: s.dateFormat ? [s.dateFormat] : [],
      fields,
    };
    if (groups.length) entry.groups = groups;
    const required = s.required.filter((r) => fields[r] !== undefined);
    if (required.length) entry.required = required;
    if (Object.keys(valueMaps).length) entry.valueMaps = valueMaps;
    return entry;
  });

  return JSON.stringify({ version: 1, sheets }, null, 2);
};

export const emptySheet = (sheet: string): SheetForm => ({
  sheet,
  include: true,
  match: sheet,
  recordKind: 'LINE',
  dateFormat: 'dd-MM-yyyy',
  allowSerial: false,
  fields: {},
  required: ['document_number', 'document_date', 'taxable_value'],
  groups: [],
  unitMap: [],
});

// ---- suggestions ------------------------------------------------------------
// A first guess from common header wording, shown as a starting point the
// user confirms column by column — never saved without being seen.

const SYNONYMS: Record<string, string[]> = {
  document_number: ['invoice no', 'invoice no.', 'invoice number', 'inv no', 'inv no.', 'bill no', 'bill no.', 'voucher no', 'voucher no.', 'vch no', 'vch no.', 'doc no', 'document no'],
  document_date: ['invoice date', 'inv date', 'bill date', 'date', 'voucher date', 'doc date'],
  counterparty_gstin: ['gstin', 'gstin/uin', 'gstin of recipient', 'customer gstin', 'party gstin', 'gst no', 'gst no.', 'gstin no'],
  counterparty_name: ['party name', 'customer name', 'name', 'particulars', 'buyer name', 'receiver name'],
  place_of_supply: ['place of supply', 'pos', 'state'],
  document_value: ['invoice value', 'invoice amount', 'bill amount', 'gross total', 'total amount', 'net amount', 'grand total'],
  hsn: ['hsn', 'hsn/sac', 'hsn code', 'sac', 'hsn/sac code'],
  description: ['description', 'item name', 'item', 'product', 'particular'],
  uqc: ['uqc', 'unit', 'uom'],
  quantity: ['qty', 'quantity'],
  rate: ['rate', 'gst rate', 'gst %', 'tax rate', 'rate %', 'gst rate %'],
  taxable_value: ['taxable value', 'taxable amount', 'taxable', 'assessable value'],
  igst: ['igst', 'igst amount', 'integrated tax'],
  cgst: ['cgst', 'cgst amount', 'central tax'],
  sgst: ['sgst', 'sgst amount', 'state tax', 'sgst/utgst', 'utgst'],
  cess: ['cess', 'cess amount'],
};

export const suggestFields = (headers: string[]): Record<string, number | null> => {
  const out: Record<string, number | null> = {};
  const norm = headers.map(normaliseHeader);
  const used = new Set<number>();
  Object.entries(SYNONYMS).forEach(([field, words]) => {
    const idx = norm.findIndex((h, i) => !used.has(i) && words.includes(h));
    if (idx >= 0) { out[field] = idx; used.add(idx); }
  });
  return out;
};

// ---- definition → form ------------------------------------------------------

type Json = Record<string, any>;

const indexOfRef = (headers: string[], ref: Json): number | null | 'unsupported' => {
  if (ref.headers || ref.indices || ref.constant !== undefined || ref.optional) return 'unsupported';
  if (typeof ref.index === 'number') return ref.index - 1 < headers.length ? ref.index - 1 : 'unsupported';
  if (typeof ref.header !== 'string') return 'unsupported';
  const want = normaliseHeader(ref.header);
  const nth = ref.occurrence ?? 1;
  let seen = 0;
  for (let i = 0; i < headers.length; i++) {
    if (normaliseHeader(headers[i]) === want && ++seen === nth) return i;
  }
  return null; // the column is not in this file — shown as unmapped
};

const globMatches = (pattern: string, name: string): boolean => {
  const re = new RegExp('^' + pattern.trim().replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*') + '$', 'i');
  return re.test(name.trim());
};

/**
 * Reads a stored definition back into the form, against this submission's
 * sheets. Returns `null` with a reason when the definition uses something the
 * form cannot show — the mapper then opens it as JSON.
 */
export const fromDefinition = (
  json: string,
  sheets: { name: string; headerCells: string[] }[],
): { form: ProfileForm } | { unsupported: string } => {
  let def: Json;
  try { def = JSON.parse(json); } catch { return { unsupported: 'the definition is not valid JSON' }; }
  if (def.version !== 1 || !Array.isArray(def.sheets)) return { unsupported: 'unknown definition version' };

  const forms: SheetForm[] = [];
  for (const s of sheets) {
    const entry: Json | undefined = def.sheets.find((e: Json) => (e.match ?? []).some((m: string) => globMatches(m, s.name)));
    if (!entry) { forms.push({ ...emptySheet(s.name), include: false }); continue; }
    if (entry.continuation) return { unsupported: 'continuation rows (lines under an invoice row)' };
    if ((entry.dateFormats ?? []).length > 1) return { unsupported: 'more than one date format on a sheet' };

    const form = emptySheet(s.name);
    form.match = (entry.match ?? [s.name])[0];
    form.recordKind = entry.recordKind ?? 'LINE';
    form.dateFormat = (entry.dateFormats ?? [])[0] ?? '';
    form.required = entry.required ?? [];

    for (const [name, ref] of Object.entries<Json>(entry.fields ?? {})) {
      const idx = indexOfRef(s.headerCells, ref);
      if (idx === 'unsupported') return { unsupported: `the column for '${name}' (summed, constant or optional columns)` };
      if (ref.dateFormats) return { unsupported: `a date format specific to '${name}'` };
      if (ref.allowSerial) form.allowSerial = true;
      form.fields[name] = idx;
    }
    for (const g of entry.groups ?? []) {
      const cols: Record<string, number | null> = {};
      for (const [name, ref] of Object.entries<Json>(g.fields ?? {})) {
        const idx = indexOfRef(s.headerCells, ref);
        if (idx === 'unsupported') return { unsupported: `rate group ${g.rate} column for '${name}'` };
        cols[name] = idx;
      }
      form.groups.push({ rate: String(g.rate), columns: cols });
    }
    const maps = entry.valueMaps ?? {};
    if (Object.keys(maps).some((k) => k !== 'uqc')) return { unsupported: 'value maps other than units' };
    form.unitMap = Object.entries<string>(maps.uqc ?? {}).map(([from, to]) => ({ from, to }));
    forms.push(form);
  }

  return { form: { sheets: forms } };
};
